let myxiaEscapeHandler = null;

function myxiaInitReveal(scope) {
  const root = scope || document;
  const revealItems = root.querySelectorAll('[data-myxia-reveal]');

  // In the theme editor a section is re-rendered without a page load, so
  // reveal it immediately rather than waiting for a scroll that never comes.
  if (window.Shopify && window.Shopify.designMode) {
    revealItems.forEach((item) => item.classList.add('visible'));
    return;
  }

  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 },
    );
    revealItems.forEach((item) => revealObserver.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('visible'));
  }
}

// Guards against binding the same element twice when the theme editor
// re-runs init after swapping a section's markup.
function myxiaBind(el, type, handler) {
  if (!el) return false;
  const flag = `myxiaBound${type}`;
  if (el.dataset && el.dataset[flag]) return false;
  if (el.dataset) el.dataset[flag] = '1';
  el.addEventListener(type, handler);
  return true;
}

// Newsletter form feedback. The browser's own validation bubble is generic
// and English-only, so it is suppressed (novalidate) and replaced with the
// section's own Spanish copy, shown in the section's message line.
function myxiaInitSignupForm() {
  document.querySelectorAll('.myxia-signup-form').forEach((form) => {
    const email = form.querySelector('input[type="email"]');
    const consent = form.querySelector('.myxia-consent input[type="checkbox"]');
    const message = form.querySelector('.myxia-form-message');
    const config = form.querySelector('[data-myxia-form-messages]');
    if (!email || !message || !config) return;

    let copy = {};
    try {
      copy = JSON.parse(config.textContent);
    } catch (error) {
      return;
    }

    const setMessage = (text, state) => {
      message.textContent = text;
      message.classList.toggle('error', state === 'error');
      message.classList.toggle('success', state === 'success');
    };

    const resetMessage = () => {
      if (!message.classList.contains('error')) return;
      setMessage(message.dataset.myxiaDefault || '', '');
    };

    // Let the field report emptiness separately from a malformed address,
    // since "you left it blank" and "that isn't an email" need different copy.
    const problem = () => {
      const value = email.value.trim();
      if (!value) return { text: copy.empty, focus: email };
      if (email.validity.typeMismatch || !email.checkValidity()) {
        return { text: copy.invalid, focus: email };
      }
      if (consent && !consent.checked) return { text: copy.consent, focus: consent };
      return null;
    };

    form.setAttribute('novalidate', 'novalidate');

    myxiaBind(form, 'submit', (event) => {
      const issue = problem();
      if (issue) {
        event.preventDefault();
        setMessage(issue.text, 'error');
        if (issue.focus) issue.focus.focus();
        return;
      }
      // Valid: the browser now navigates, so this is the last paint the
      // visitor sees before Shopify's redirect brings back the real result.
      setMessage(copy.sending, '');
    });

    myxiaBind(email, 'input', resetMessage);
    if (consent) myxiaBind(consent, 'change', resetMessage);
  });
}

// Product hero gallery: swap the active large image + active thumb, with
// simple wraparound prev/next arrows. Keyboard-operable via native <button>
// elements (thumbs use role="tab"/aria-selected, arrows have aria-labels).
function myxiaInitProductGallery(scope) {
  const root = scope || document;
  root.querySelectorAll('.myxia-product-hero__stage').forEach((stage) => {
    if (stage.dataset.myxiaGalleryBound) return;
    stage.dataset.myxiaGalleryBound = '1';

    const wrapper = stage.closest('.myxia-product-hero__media');
    if (!wrapper) return;
    const slides = Array.from(stage.querySelectorAll('[data-myxia-hero-slide]'));
    const thumbs = Array.from(wrapper.querySelectorAll('[data-myxia-hero-thumb]'));
    if (slides.length < 2) return;

    let activeIndex = 0;

    function showSlide(index) {
      const nextIndex = (index + slides.length) % slides.length;
      slides[activeIndex].hidden = true;
      slides[activeIndex].classList.remove('is-active');
      thumbs[activeIndex] && thumbs[activeIndex].classList.remove('is-active');
      thumbs[activeIndex] && thumbs[activeIndex].setAttribute('aria-selected', 'false');

      activeIndex = nextIndex;
      slides[activeIndex].hidden = false;
      slides[activeIndex].classList.add('is-active');
      thumbs[activeIndex] && thumbs[activeIndex].classList.add('is-active');
      thumbs[activeIndex] && thumbs[activeIndex].setAttribute('aria-selected', 'true');
    }

    myxiaBind(stage.querySelector('[data-myxia-hero-prev]'), 'click', () => showSlide(activeIndex - 1));
    myxiaBind(stage.querySelector('[data-myxia-hero-next]'), 'click', () => showSlide(activeIndex + 1));
    thumbs.forEach((thumb, index) => {
      myxiaBind(thumb, 'click', () => showSlide(index));
    });
  });
}

// Purchase panel: subscribe/one-time toggle shows or hides the delivery
// frequency row, and the plan cards set the real hidden quantity input used
// by the actual add-to-cart form (buy-buttons.liquid), rather than forking
// cart submission logic.
function myxiaInitPurchasePanel(scope) {
  const root = scope || document;
  root.querySelectorAll('.myxia-purchase-panel').forEach((panel) => {
    if (panel.dataset.myxiaPurchaseBound) return;
    panel.dataset.myxiaPurchaseBound = '1';

    const frequencyRow = panel.querySelector('[data-myxia-frequency-row]');
    const purchaseTypeInputs = panel.querySelectorAll('[data-myxia-purchase-type]');
    purchaseTypeInputs.forEach((input) => {
      myxiaBind(input, 'change', () => {
        if (!frequencyRow) return;
        const isSubscribe = panel.querySelector('[data-myxia-purchase-type]:checked')?.value === 'subscribe';
        frequencyRow.hidden = !isSubscribe;
      });
    });

    const quantityInput = panel.querySelector('.product-form__buttons input[name="quantity"]')
      || panel.querySelector('input[name="quantity"]');
    const planOptions = panel.querySelectorAll('[data-myxia-plan-option]');
    planOptions.forEach((input) => {
      myxiaBind(input, 'change', () => {
        planOptions.forEach((option) => {
          const card = option.closest('.myxia-plan-card');
          if (card) card.classList.toggle('is-selected', option.checked);
        });
        if (quantityInput && input.checked) {
          quantityInput.value = input.value;
        } else if (input.checked) {
          // The reused buy-buttons.liquid snippet has no quantity input by
          // default (Dawn's quantity selector is a separate opt-in block),
          // so create a hidden one the first time a plan card is chosen.
          const form = panel.querySelector('form[data-type="add-to-cart-form"]');
          if (form && !form.querySelector('input[name="quantity"]')) {
            const hiddenQuantity = document.createElement('input');
            hiddenQuantity.type = 'hidden';
            hiddenQuantity.name = 'quantity';
            hiddenQuantity.value = input.value;
            form.appendChild(hiddenQuantity);
          } else if (form) {
            form.querySelector('input[name="quantity"]').value = input.value;
          }
        }
      });
    });
  });
}

// Sticky mobile add-to-cart bar: the OPPOSITE of [data-myxia-reveal]'s
// "fade in once on scroll into view" — this bar starts hidden and is shown
// only while the real add-to-cart button (inside the purchase panel form)
// has scrolled OUT of view, then hidden again once it's back in view.
// Clicking the bar's own button just proxies a click to the real button so
// Shopify's real product-form.js / AJAX cart flow runs, nothing is forked.
function myxiaInitStickyAtc(scope) {
  const root = scope || document;
  const bar = root.querySelector('[data-myxia-sticky-atc]');
  if (!bar || bar.dataset.myxiaStickyBound) return;

  const realButton = document.querySelector('.myxia-purchase-panel__form .product-form__submit');
  if (!realButton) return;
  bar.dataset.myxiaStickyBound = '1';

  const barButton = bar.querySelector('[data-myxia-sticky-atc-button]');
  myxiaBind(barButton, 'click', () => realButton.click());

  if (window.Shopify && window.Shopify.designMode) return; // no scroll in the editor preview

  // The real button sits further down the page than the initial viewport on
  // most screens, so it starts out "not intersecting" before the visitor has
  // ever scrolled to it. Only start reacting to intersection changes once
  // the button has been seen at least once, so the bar doesn't wrongly
  // appear before the visitor has even reached the real add-to-cart button.
  let hasBeenSeen = false;

  function applyVisibility(isIntersecting) {
    if (isIntersecting) hasBeenSeen = true;
    if (!hasBeenSeen) return;
    const isVisible = !isIntersecting;
    bar.classList.toggle('is-visible', isVisible);
    bar.setAttribute('aria-hidden', String(!isVisible));
  }

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => applyVisibility(entry.isIntersecting));
      },
      { threshold: 0 },
    );
    observer.observe(realButton);
  }

  // Belt-and-braces scroll listener: some browsers/embedded webviews throttle
  // or coalesce IntersectionObserver callbacks more aggressively than a
  // plain scroll handler, which would leave the bar stuck in a stale state.
  // Reading getBoundingClientRect() on scroll is cheap for a single element
  // and keeps this in sync even if the observer above lags.
  let scrollTicking = false;
  myxiaBind(window, 'scroll', () => {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      const rect = realButton.getBoundingClientRect();
      const isIntersecting = rect.bottom > 0 && rect.top < window.innerHeight;
      applyVisibility(isIntersecting);
      scrollTicking = false;
    });
  });
}

// Cross-sell carousel arrows (separate from the homepage's
// .myxia-carousel-arrow.previous/.next bindings, which target the
// homepage-only .myxia-product-carousel by a shared class — this section
// uses its own data attributes so both can exist without clashing).
function myxiaInitCrossSellCarousel(scope) {
  const root = scope || document;
  root.querySelectorAll('[data-myxia-cross-sell-prev]').forEach((prevButton) => {
    const shell = prevButton.closest('.myxia-carousel-shell');
    const carousel = shell && shell.querySelector('.myxia-product-carousel');
    const nextButton = shell && shell.querySelector('[data-myxia-cross-sell-next]');
    if (!carousel) return;
    myxiaBind(prevButton, 'click', () => {
      carousel.scrollBy({ left: -Math.max(carousel.clientWidth * 0.72, 300), behavior: 'smooth' });
    });
    myxiaBind(nextButton, 'click', () => {
      carousel.scrollBy({ left: Math.max(carousel.clientWidth * 0.72, 300), behavior: 'smooth' });
    });
  });
}

// Item 18: floating back-to-top button. Appears once the reviews section
// ([data-myxia-back-to-top-trigger]) scrolls into view, hidden again if the
// visitor scrolls back above it, and scrolls to top on click. Independent
// of [data-myxia-reveal] (which only ever reveals once and never hides
// again) since this needs to toggle both ways with scroll position.
function myxiaInitBackToTop(scope) {
  const root = scope || document;
  const button = root.querySelector('[data-myxia-back-to-top]');
  const trigger = document.querySelector('[data-myxia-back-to-top-trigger]');
  if (!button || !trigger || button.dataset.myxiaBackToTopBound) return;
  button.dataset.myxiaBackToTopBound = '1';

  myxiaBind(button, 'click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  if (window.Shopify && window.Shopify.designMode) return; // no scroll in the editor preview

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          button.classList.toggle('is-visible', entry.isIntersecting);
        });
      },
      { threshold: 0 },
    );
    observer.observe(trigger);
  }
}

function myxiaInit() {
  myxiaInitReveal();
  myxiaInitSignupForm();
  myxiaInitProductGallery();
  myxiaInitPurchasePanel();
  myxiaInitStickyAtc();
  myxiaInitCrossSellCarousel();
  myxiaInitBackToTop();

  const menuTriggers = document.querySelectorAll('.myxia-menu-trigger');
  menuTriggers.forEach((trigger) => {
    myxiaBind(trigger, 'click', () => {
      const panel = trigger.nextElementSibling;
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      menuTriggers.forEach((other) => {
        other.setAttribute('aria-expanded', 'false');
        other.nextElementSibling.classList.remove('open');
      });
      trigger.setAttribute('aria-expanded', String(!isOpen));
      panel.classList.toggle('open', !isOpen);
    });
  });

  const mobileToggle = document.querySelector('.myxia-mobile-toggle');
  const mobileMenu = document.querySelector('.myxia-mobile-menu');

  function closeMobileMenu() {
    if (!mobileToggle || !mobileMenu) return;
    mobileToggle.setAttribute('aria-expanded', 'false');
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.classList.remove('open');
    document.body.classList.remove('menu-open');
  }

  if (mobileToggle && mobileMenu) {
    myxiaBind(mobileToggle, 'click', () => {
      const isOpen = mobileToggle.getAttribute('aria-expanded') === 'true';
      mobileToggle.setAttribute('aria-expanded', String(!isOpen));
      mobileMenu.setAttribute('aria-hidden', String(isOpen));
      mobileMenu.classList.toggle('open', !isOpen);
      document.body.classList.toggle('menu-open', !isOpen);
    });
    mobileMenu.querySelectorAll('a').forEach((link) => myxiaBind(link, 'click', closeMobileMenu));
  }

  const carousel = document.querySelector('.myxia-product-carousel');
  const previousButton = document.querySelector('.myxia-carousel-arrow.previous');
  const nextButton = document.querySelector('.myxia-carousel-arrow.next');

  function moveCarousel(direction) {
    if (!carousel) return;
    carousel.scrollBy({
      left: direction * Math.max(carousel.clientWidth * 0.72, 300),
      behavior: 'smooth',
    });
  }
  myxiaBind(previousButton, 'click', () => moveCarousel(-1));
  myxiaBind(nextButton, 'click', () => moveCarousel(1));

  const chatButton = document.querySelector('.myxia-chat-button');
  const chatPanel = document.querySelector('.myxia-chat-panel');
  const chatClose = document.querySelector('.myxia-chat-close');

  function closeChat() {
    if (!chatButton || !chatPanel) return;
    chatButton.setAttribute('aria-expanded', 'false');
    chatPanel.setAttribute('aria-hidden', 'true');
    chatPanel.classList.remove('open');
  }

  if (chatButton && chatPanel) {
    myxiaBind(chatButton, 'click', () => {
      const isOpen = chatButton.getAttribute('aria-expanded') === 'true';
      chatButton.setAttribute('aria-expanded', String(!isOpen));
      chatPanel.setAttribute('aria-hidden', String(isOpen));
      chatPanel.classList.toggle('open', !isOpen);
    });
  }
  myxiaBind(chatClose, 'click', closeChat);

  const bagButton = document.querySelector('.myxia-bag-button');
  if (bagButton) {
    myxiaBind(bagButton, 'click', () => {
      const target = document.querySelector('#myxia-acceso');
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    });
  }

  // document is shared, so track the handler rather than the element.
  if (myxiaEscapeHandler) document.removeEventListener('keydown', myxiaEscapeHandler);
  myxiaEscapeHandler = (event) => {
    if (event.key !== 'Escape') return;
    closeMobileMenu();
    closeChat();
    document.querySelectorAll('.myxia-menu-trigger').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      if (trigger.nextElementSibling) trigger.nextElementSibling.classList.remove('open');
    });
  };
  document.addEventListener('keydown', myxiaEscapeHandler);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', myxiaInit);
} else {
  myxiaInit();
}

// The theme editor swaps a section's markup without reloading the page, so
// re-bind that section's behaviour and reveal its content.
document.addEventListener('shopify:section:load', (event) => {
  myxiaInitReveal(event.target);
  myxiaInit();
});

document.addEventListener('shopify:section:select', (event) => {
  myxiaInitReveal(event.target);
});
